'use client'

import { useThrottledValue } from '@mantine/hooks'
import { LineCloseCircle } from '@qr-front/icons/line-close-circle'
import { LineSearch } from '@qr-front/icons/line-search'
import { Checkbox } from '@qr-front/ui/components/Checkbox'
import { Input } from '@qr-front/ui/components/Input'
import {
	FiltersSheet,
	TFilterConfig,
} from '@qr-front/ui/dashboard/components/FiltersSheet'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import {
	useCallback,
	useDeferredValue,
	useEffect,
	useMemo,
	useState,
} from 'react'

import { disabledColDef, nameColDef, previewColDef } from '../columnsConfig'
import { tableAtom } from '../tableState'
import { DesktopFilters } from './DesktopFilters'
import {
	getQrTypeOptions,
	getSortOptions,
	getSortValueFromState,
	getStatusOptions,
	parseSortValue,
} from './filterOptions'

export const TableControls = () => {
	const t = useTranslations()

	const [searchQuery, setSearchQuery] = useState('')
	const deferredSearch = useDeferredValue(searchQuery)
	const throttledSearch = useThrottledValue(deferredSearch, 500)
	const table = useAtomValue(tableAtom)

	useEffect(() => {
		table.getColumn(nameColDef.id!)?.setFilterValue(throttledSearch)
	}, [throttledSearch, table])

	const sortState = table.getState().sorting[0]
	const selectedSortValue =
		getSortValueFromState(sortState?.id, sortState?.desc) || 'created-desc'
	const selectedStatusValues = table
		.getColumn(disabledColDef.id!)
		?.getFilterValue() as string[] | undefined
	const selectedQrTypeValues = table
		.getColumn(previewColDef.id!)
		?.getFilterValue() as string[] | undefined

	const totalActiveFilters =
		(selectedStatusValues?.length || 0) + (selectedQrTypeValues?.length || 0)

	const handleSortChange = useCallback(
		(value: string) => {
			const parsed = parseSortValue(value)
			if (parsed) table.setSorting([parsed])
		},
		[table],
	)

	const handleStatusChange = useCallback(
		(value: string) => {
			const currentValues =
				(table.getColumn(disabledColDef.id!)?.getFilterValue() as string[]) ??
				[]
			const newValues = currentValues.includes(value)
				? currentValues.filter((v) => v !== value)
				: [...currentValues, value]
			table.getColumn(disabledColDef.id!)?.setFilterValue(newValues)
		},
		[table],
	)

	const handleQrTypeChange = useCallback(
		(value: string) => {
			const currentValues =
				(table.getColumn(previewColDef.id!)?.getFilterValue() as string[]) ?? []
			const newValues = currentValues.includes(value)
				? currentValues.filter((v) => v !== value)
				: [...currentValues, value]
			table.getColumn(previewColDef.id!)?.setFilterValue(newValues)
		},
		[table],
	)

	const handleClearAll = useCallback(() => {
		table.resetColumnFilters()
		table.resetSorting()
		setSearchQuery('')
	}, [table, setSearchQuery])

	const filters: TFilterConfig[] = useMemo(
		() => [
			{
				title: t('dashboard__qr_table__controls__sort_placeholder'),
				type: 'radio',
				options: getSortOptions(t),
				onChange: handleSortChange,
				selectedValue: selectedSortValue,
			},
			{
				title: t('dashboard__qr_table__controls__status_placeholder'),
				type: 'chips',
				options: getStatusOptions(t),
				onChange: handleStatusChange,
				selectedValues: selectedStatusValues,
			},
			{
				title: t('dashboard__qr_table__controls__type_placeholder'),
				type: 'chips',
				options: getQrTypeOptions(t),
				onChange: handleQrTypeChange,
				selectedValues: selectedQrTypeValues,
			},
		],
		[
			t,
			selectedSortValue,
			selectedStatusValues,
			selectedQrTypeValues,
			handleSortChange,
			handleStatusChange,
			handleQrTypeChange,
		],
	)

	const globalSelectCheckbox = (
		<Checkbox
			checked={
				table.getIsAllPageRowsSelected() ||
				(table.getIsSomePageRowsSelected() && 'indeterminate')
			}
			onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
		/>
	)

	const handleClearInput = () => {
		setSearchQuery('')
	}

	const placeholder = t('dashboard__qr_table__controls__search_placeholder')

	return (
		<div className="flex items-center gap-4 rounded-10 bg-white px-4 desktop:mt-4 desktop:bg-transparent">
			{globalSelectCheckbox}
			<Input
				className={cn(
					'type-body-medium h-10 flex-1 rounded-8 pl-10 desktop:type-body-small desktop:w-60',
					getVersionForClone({
						base: '',
						clone1: '[&:not(textarea)]:rounded-8',
					}),
				)}
				containerClassName="flex-1 desktop:flex-none"
				placeholder={placeholder}
				value={searchQuery}
				leftIcon={() => <LineSearch className="w-4" />}
				iconSeparator={false}
				onChange={(event) => setSearchQuery(event.target.value)}
				{...(searchQuery && {
					rightIcon: () => <LineCloseCircle className="w-4 cursor-pointer" />,
					rightOnClick: handleClearInput,
				})}
			/>
			<div className="hidden h-6 max-h-6 w-px shrink-0 bg-border-grey-50 desktop:inline-flex" />
			<DesktopFilters table={table} onClear={handleClearInput} />
			<FiltersSheet
				filters={filters}
				onClearAll={handleClearAll}
				totalActiveFilters={totalActiveFilters}
				translations={{
					title: t('dashboard__qr_table__controls__filters'),
					clearAllButton: t('dashboard__qr_table__controls__clear_all'),
					seeResultsButton: t('dashboard__qr_table__controls__see_results'),
				}}
				className="py-4 desktop:hidden"
			/>
		</div>
	)
}
